import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import {
  getCategories,
  checkCodes,
  addItem,
  updateItem,
  addPrefix,
  getPrefix,
  getUnits,
  addLocation,
  getLocations,
} from "../../services/services";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { ChevronDown } from "lucide-react";
import { FaChevronDown } from "react-icons/fa";
import PrefixModal from "../Modal/PrefixModal";

const gstRates = [0, 5, 12, 18, 28];

const AddItem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [metaFields, setMetaFields] = useState([{ key: "", value: "" }]);

  const editProduct = location.state?.product ?? {};
  const isPurchase = location.state?.isPurchase ?? false;
  const isEditMode = !!editProduct._id;
  const batchData = editProduct.batches?.[0] || {};
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      const existingMeta = Array.isArray(editProduct.meta)
        ? editProduct.meta
        : [];

      setMetaFields(
        existingMeta.length > 0
          ? existingMeta.map((item) => ({
              key: item.key || "",
              value: item.value || "",
            }))
          : [{ key: "", value: "" }],
      );
    } else {
      setMetaFields([{ key: "", value: "" }]);
    }
  }, [isEditMode, editProduct._id]);

  const [units, setUnits] = useState([]);

  const selectedBusiness = JSON.parse(localStorage.getItem("selectedBusiness"));
  const dbName = selectedBusiness?.tenantDB;

  const imageUrl = editProduct?.image
    ? `${process.env.REACT_APP_IMAGE_URL}/uploads/${dbName}/product_images/${editProduct.image}`
    : null;
  const nameRef = useRef(null);
  const quantityRef = useRef(null);
  const purchasePriceRef = useRef(null);
  const imageInputRef = useRef(null);
  const firstLoad = useRef(true);

  const [product, setProduct] = useState({
    name: editProduct?.name ?? "",
    productCode: editProduct?.productCode ?? "",
    hsnCode: editProduct?.hsnCode ?? "",
    category: editProduct?.category?._id ?? editProduct?.category ?? "",
    description: editProduct?.description ?? "",
    unit: editProduct?.unit?._id ?? editProduct?.unit ?? "",
    minStockAlert: editProduct?.minStockAlert ?? "",
    salePrice: editProduct?.salePrice ?? "",
    taxType: editProduct?.taxType ?? "exclusive",
    gstRate: editProduct?.gstRate ?? "",
    location: editProduct?.location ?? "",
    discount: editProduct?.discount ?? "",
    image: imageUrl,
    imageFile: null,
    totalRemainingQty: editProduct?.totalRemainingQty,

    batch: {
      quantity: batchData?.quantity ?? "",
      purchasePrice: batchData?.purchasePrice ?? "",
      taxType: batchData?.taxType ?? "exclusive",
      purchaseDate: batchData?.purchaseDate
        ? new Date(batchData.purchaseDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      expiryDate: batchData?.expiryDate
        ? new Date(batchData.expiryDate).toISOString().split("T")[0]
        : "",
    },

    remainingQty:
      editProduct?.totalRemainingQty ??
      batchData?.remainingQty ??
      batchData?.quantity ??
      0,

    prefix: "",
    manualCode: "",

    //metaFields: editProduct.meta ?? [],
  });

  const [categories, setCategories] = useState([]);
  const [codeError, setCodeError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [locationOpen, setLocationOpen] = useState(false);
  const [searchLocation, setSearchLocation] = useState("");
  const locationDropdownRef = useRef(null);
  const [locations, setLocations] = useState([]);
  const [prefixList, setPrefixList] = useState(() => {
    const storedPrefixes = localStorage.getItem("prefixList");
    return storedPrefixes ? JSON.parse(storedPrefixes) : ["noPrefix"];
  });
  const [newModalValue, setNewModalValue] = useState({
    name: "",
    type: "product",
  });
  const [additionalStock, setAdditionalStock] = useState(0);
  const [isImageRemoved, setIsImageRemoved] = useState(false);

  const fetchLocations = async () => {
    try {
      const res = await getLocations({});

      if (res.data?.success) {
        setLocations(res.data.data);
      }
    } catch (err) {
      toast.error("Failed to load locations");
    }
  };
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(e.target)
      ) {
        setLocationOpen(false);
        setSearchLocation("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isEditMode) return;

    const code = editProduct?.productCode;

    if (!code) {
      // Optional: toast.warning("Product data not found. Please go back and try again.");
      return;
    }

    let prefix = "noPrefix";
    let manual = code;

    if (code.includes("-")) {
      const parts = code.split("-");
      prefix = parts[0];
      manual = parts.slice(1).join("-");
    }

    setProduct((p) => ({
      ...p,
      productCode: code,
      prefix,
      manualCode: manual,
      discount: editProduct.discount || "",
      image: imageUrl || null,
    }));
  }, [isEditMode, editProduct, imageUrl]); // added imageUrl dependency

  const handleCodeCheck = useCallback(
    debounce(async (code) => {
      if (!code.trim()) {
        setCodeError("");
        return;
      }
      try {
        const { data } = await checkCodes({ code });
        setCodeError(data.valid ? "" : data.message);
      } catch {
        setCodeError("Error checking code");
      }
    }, 600),
    [],
  );

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      if (file.size > 0.5 * 1024 * 1024) {
        toast.error("Image must be less than 500KB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProduct((p) => ({
          ...p,
          imageFile: file,
          image: reader.result,
        }));
        setIsImageRemoved(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setProduct((p) => ({
      ...p,
      image: null,
      imageFile: null,
    }));
    setIsImageRemoved(true);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };
  const handleBulkUpload = () => {
    navigate("/items/bulkupload");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("batch.")) {
      const field = name.replace("batch.", "");

      setProduct((prev) => ({
        ...prev,
        batch: {
          ...prev.batch,
          [field]: value,
        },
      }));
      return;
    }

    setProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }

    // Safe guard
    const currentCode = product?.productCode?.trim();
    if (currentCode) {
      handleCodeCheck(currentCode);
    } else {
      setCodeError("");
    }
  }, [product?.productCode]);

  const validate = () => {
    const newErrors = {};
    let firstInvalidRef = null;
    let firstErrorMessage = "";

    if (!product.name.trim()) {
      newErrors.name = "Item name is required";
      firstInvalidRef = nameRef;
      firstErrorMessage = newErrors.name;
    }

    setErrors(newErrors);

    // Focus + scroll
    if (firstInvalidRef?.current) {
      firstInvalidRef.current.focus();
      firstInvalidRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    return {
      isValid: Object.keys(newErrors).length === 0,
      message: firstErrorMessage,
    };
  };

  const buildPayload = () => {
    const batchData = {
      quantity: Number(product.batch.quantity) || 0,
      remainingQty:
        Number(product.batch.remainingQty ?? product.batch.quantity) || 0,
      purchasePrice: Number(product.batch.purchasePrice) || 0,
      taxType: product.batch.taxType || "exclusive",
      purchaseDate: product.batch.purchaseDate || new Date().toISOString(),
      expiryDate: product.batch.expiryDate || null,
    };

    // Generate final productCode for new products
    let finalProductCode = product.productCode?.trim();

    // If user selected prefix + manual code, ensure it's combined
    if (!finalProductCode && product.prefix && product.manualCode) {
      finalProductCode =
        product.prefix === "noPrefix"
          ? product.manualCode.trim()
          : `${product.prefix}-${product.manualCode.trim()}`;
    }

    const payload = {
      name: product.name.trim(),
      category: product.category || undefined,
      unit: product.unit || undefined,
      description: product.description || "",
      productCode: finalProductCode || undefined,
      hsnCode: product.hsnCode?.trim() || undefined,
      minStockAlert: Number(product.minStockAlert) || 0,
      salePrice: Number(product.salePrice) || 0,
      taxType: product.taxType,
      gstRate: Number(product.gstRate) || 0,
      location: product.location || undefined,
      discount: Number(product.discount) || 0,

      meta: metaFields
        .filter((item) => item.key?.trim() && item.value?.trim())
        .map((item) => ({
          key: item.key.trim(),
          value: item.value.trim(),
        })),
    };

    // Image logic
    if (product.imageFile) {
      payload.image = product.imageFile;
    } else if (isImageRemoved) {
      payload.removeImage = true;
    }

    if (!isEditMode) {
      payload.batches = [batchData];
    } else {
      payload.purchasePrice = Number(product.batch.purchasePrice) || 0;
      payload.purchaseDate =
        product.batch.purchaseDate || new Date().toISOString();
      payload.expiryDate = product.batch.expiryDate || null;

      const currentQty = editProduct?.totalRemainingQty || 0;
      const newQty = Number(product.remainingQty) || 0;
      payload.additionalStock = newQty - currentQty;
      // payload.remainingQty = Number(product.remainingQty) || 0;
    }

    // Clean only truly empty values (but keep productCode even if empty string)
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined || payload[key] === null) {
        delete payload[key];
      }
    });

    return payload;
  };
  const handleSubmit = async () => {
    const { isValid, message } = validate();
    if (!isValid) {
      toast.error(message);
      return;
    }

    // Final check for duplicate code
    if (!isEditMode && product.productCode) {
      try {
        const { data } = await checkCodes({ code: product.productCode });
        if (!data.valid) {
          setCodeError(data.message || "Product code already exists");
          toast.error(data.message || "Product code already exists");
          return; // stop submission
        }
      } catch (err) {
        toast.error("Error checking product code");
        return;
      }
    }

    const payload = buildPayload();

    const formData = new FormData();

    Object.keys(payload).forEach((key) => {
      if (key === "meta") {
        formData.append("meta", JSON.stringify(payload.meta));
      } else if (key === "batches") {
        formData.append("batches", JSON.stringify(payload.batches));
      } else if (key === "image" && payload.image instanceof File) {
        formData.append("image", payload.image);
      } else {
        formData.append(key, payload[key]);
      }
    });

    try {
      if (isEditMode) {
        await updateItem(editProduct._id, formData);
        toast.success("Item updated successfully");
      } else {
        await addItem(formData);
        toast.success("Item added successfully");
      }
      navigate(-1);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || "Failed to save item");
    }
  };

  useEffect(() => {
    const fetchPrefixes = async () => {
      try {
        const { data } = await getPrefix("product");
        const list = data.prefixes.map((p) => p.name);

        const finalList = ["noPrefix", ...list];

        setPrefixList(finalList);

        localStorage.setItem("prefixList", JSON.stringify(finalList));
      } catch (err) {
        console.log(err);
        toast.error("Failed to load prefixes");
      }
    };

    fetchPrefixes();
  }, []);

  const handleAddPrefix = async () => {
    const name =
      typeof newModalValue === "string"
        ? newModalValue.trim()
        : newModalValue?.name?.trim();

    if (!name) return;

    try {
      const res = await addPrefix({
        name,
        type: newModalValue?.type || "product",
      });

      const newName = res.data.prefix.name;

      const updated = [...prefixList, newName];
      setPrefixList(updated);
      localStorage.setItem("prefixList", JSON.stringify(updated));

      // AUTO SELECT NEW PREFIX
      setProduct((prev) => {
        const manual = prev.manualCode || "";

        return {
          ...prev,
          prefix: newName,
          productCode:
            newName === "noPrefix"
              ? manual
              : manual
                ? `${newName}-${manual}`
                : "",
        };
      });

      setShowAddModal(false);
      setNewModalValue("");
      toast.success("Prefix added");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add prefix");
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await getUnits({ search: "" });
      if (res.data.status) {
        // setUnits(res.data.response.units);
        setUnits(
          (res.data.response.units || []).filter(
            (unit) => unit.status === "active",
          ),
        );
      }
    } catch (err) {
      toast.error("Failed to load units");
    }
  };

  const fetchCategories = async () => {
    const payload = { type: "item" };
    try {
      const res = await getCategories(payload);
      // console.log(res);
      if (res.data.status) {
        // setCategories(res.data.response);
        setCategories(
          (res.data.response || []).filter((cat) => cat.status === "active"),
        );
      }
    } catch (err) {
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    fetchUnits();
    fetchCategories();
    fetchLocations();
  }, []);

  const handleAddLocation = async () => {
    const name = newModalValue.trim();
    if (!name) return;

    try {
      const res = await addLocation({ name });

      if (res.data?.success) {
        const newLoc = res.data.data.name;

        setProduct((p) => ({
          ...p,
          location: newLoc,
        }));

        fetchLocations(); // refresh list

        toast.success(`Location "${newLoc}" added`);

        setNewModalValue("");
        setShowAddModal(false);
        setModalType("");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add location");
    }
  };

  const handleAddMetaField = () => {
    setMetaFields((prev) => [...prev, { key: "", value: "" }]);
  };

  const handleMetaChange = (index, field, value) => {
    setMetaFields((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const handleDeleteMetaField = (index) => {
    setMetaFields((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div id="main" className="add-item-page ">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 custom-title">
          {isEditMode
            ? isPurchase
              ? "Edit Purchase Product"
              : "Edit Product"
            : isPurchase
              ? "Add Purchase Product"
              : "Add New Product"}
        </h4>

        {!isEditMode && (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2 mb-0"
              onClick={handleBulkUpload}
            >
              <i className="bi bi-upload d-inline d-md-none"></i>
              <span className="d-none d-md-inline">Bulk Upload</span>
            </button>
          </div>
        )}
      </div>
      <div className="content-wrapper">
        <div className="row card">
          <div className="card-body">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="mb-4">
                {product.image ? (
                  <div className="mt-2 position-relative d-inline-block">
                    <img
                      src={product.image}
                      alt="Preview"
                      className="rounded shadow-sm"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                      }}
                    />
                    <div className="position-absolute top-0 end-0">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger fs-4 d-flex align-items-center justify-content-center"
                        style={{
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          marginTop: "2px",
                        }}
                        onClick={removeImage}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="mt-2 border rounded d-flex align-items-center justify-content-center bg-light"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <i
                      className="bi bi-image text-muted"
                      style={{ fontSize: "2rem" }}
                    ></i>
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Product Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    ref={imageInputRef}
                    onChange={handleImageChange}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    {isPurchase ? "Select Product" : "Product Name"}
                    <span className="text-danger ms-1">*</span>
                  </label>

                  <input
                    ref={nameRef}
                    type="text"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    name="name"
                    value={product.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Product Code</label>

                  {/* PREFIX SELECTOR */}
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <select
                      className="form-select w-auto"
                      value={product.prefix}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "addNew") {
                          setModalType("prefix");
                          setShowAddModal(true);
                        } else {
                          setProduct((p) => {
                            const oldCode = p.productCode || "";
                            const manual = p.manualCode || "";

                            let newCode = oldCode;

                            // Only regenerate if user already has manual input
                            if (manual) {
                              newCode =
                                v === "noPrefix" ? manual : `${v}-${manual}`;
                            }

                            return {
                              ...p,
                              prefix: v,
                              productCode: newCode,
                            };
                          });
                        }
                      }}
                    >
                      {prefixList.map((p) => (
                        <option key={p} value={p}>
                          {p === "noPrefix" ? "No Prefix" : p}
                        </option>
                      ))}
                      <option value="addNew" className="text-primary">
                        + Add New Prefix
                      </option>
                    </select>
                    {/* FINAL CODE + MANUAL ENTRY + GENERATE BUTTON */}
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control ${
                          codeError ? "is-invalid" : ""
                        }`}
                        value={product.manualCode.toUpperCase()}
                        onChange={(e) => {
                          // Allow A-Z, 0-9, hyphen and convert to uppercase
                          const val = e.target.value.replace(
                            /[^a-zA-Z0-9-]/g,
                            "",
                          );
                          const prefix =
                            product.prefix && product.prefix !== "noPrefix"
                              ? product.prefix
                              : "";

                          setProduct((p) => ({
                            ...p,
                            manualCode: val,
                            productCode:
                              prefix && prefix !== "noPrefix"
                                ? `${prefix}-${val}`
                                : val,
                          }));
                        }}
                      />
                      {codeError && (
                        <div className="invalid-feedback">{codeError}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">HSN Code</label>
                  <input
                    type="text"
                    className="form-control"
                    name="hsnCode"
                    value={product.hsnCode.toUpperCase()}
                    onChange={(e) => {
                      let val = e.target.value.toUpperCase();

                      // Allow only A-Z, 0-9 and hyphen
                      val = val.replace(/[^A-Z0-9-]/g, "");

                      handleInputChange({
                        target: { name: "hsnCode", value: val },
                      });
                    }}
                    maxLength={8}
                  />
                </div>
              </div>
              {/* ---- Category & Unit ---- */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Category </label>
                  <select
                    className="form-select"
                    name="category"
                    value={product.category}
                    onChange={handleInputChange}
                  >
                    {categories.length == 0 ? (
                      <option value="">No categories</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select category
                        </option>
                        {categories.map((cat) => (
                          <option key={cat?._id} value={cat?._id}>
                            {cat?.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Unit</label>
                  <select
                    className="form-select"
                    name="unit"
                    value={product.unit}
                    onChange={handleInputChange}
                  >
                    {units.length == 0 ? (
                      <option value="">No units</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select unit
                        </option>
                        {units.map((un) => (
                          <option key={un?._id} value={un?.name}>
                            {un?.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
              </div>
              {/* ---- Quantity/InStock & Prices ---- */}
              <div className="row">
                {!isEditMode && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Quantity (In Stock)
                      {/* <span className="text-danger ms-1">*</span> */}
                    </label>
                    <input
                      ref={quantityRef}
                      type="text"
                      inputMode="numeric"
                      className={`form-control ${
                        errors.quantity ? "is-invalid" : ""
                      }`}
                      name="batch.quantity"
                      value={product.batch.quantity || ""}
                      onChange={handleInputChange}
                    />
                    {errors.quantity && (
                      <div className="invalid-feedback">{errors.quantity}</div>
                    )}
                  </div>
                )}
                {isEditMode && (
                  <>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Current Stock</label>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {/* Minus button */}
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() =>
                            setProduct((prev) => ({
                              ...prev,
                              remainingQty: Math.max(
                                0,
                                Number(prev.remainingQty) - 1,
                              ),
                            }))
                          }
                        >
                          -
                        </button>

                        {/* Input Box */}
                        <input
                          type="number"
                          min="0"
                          value={product.remainingQty || 0}
                          onChange={(e) =>
                            setProduct((prev) => ({
                              ...prev,
                              remainingQty: Number(e.target.value),
                            }))
                          }
                          className="form-control"
                          style={{ width: "350px", textAlign: "center" }}
                        />

                        {/* Plus button */}
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() =>
                            setProduct((prev) => ({
                              ...prev,
                              remainingQty: Number(prev.remainingQty) + 1,
                            }))
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </>
                )}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Min Stock Alert</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    name="minStockAlert"
                    value={product.minStockAlert}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>
              {/* Purchase Price & Sale Price */}
              {/* <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label ">
                    Purchase Price (₹)
                  </label>
                  <div className="input-group">
                    <input
                      ref={purchasePriceRef}
                      type="text"
                      inputMode="decimal"
                      className={`form-control ${
                        errors.purchasePrice ? "is-invalid" : ""
                      }`}
                      name="batch.purchasePrice"
                      value={product.batch.purchasePrice}
                      onChange={handleInputChange}
                    />
                    {errors.purchasePrice && (
                      <div className="invalid-feedback">
                        {errors.purchasePrice}
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn border d-flex align-items-center  "
                      style={{ whiteSpace: "nowrap" }}
                      onClick={() =>
                        setProduct((p) => ({
                          ...p,
                          taxType:
                            p.taxType === "exclusive"
                              ? "inclusive"
                              : "exclusive",
                        }))
                      }
                    >
                      {product.taxType === "exclusive" ? (
                        <>
                          Exclusive <ChevronDown size={16} className="ms-1" />
                        </>
                      ) : (
                        <>
                          Inclusive <ChevronDown size={16} className="ms-1" />
                        </>
                      )}
                    </button>
                    {product.batch.purchasePrice && product.gstRate ? (
                      <div
                        className="p-2 rounded-end border bg-light"
                        style={{ fontSize: "0.9rem" }}
                      >
                        <div className="d-flex  justify-content-between">
                          <strong>
                            ₹
                            {product.taxType === "exclusive"
                              ? (
                                  product.batch.purchasePrice *
                                  (1 + product.gstRate / 100)
                                ).toFixed(2)
                              : (
                                  product.batch.purchasePrice /
                                  (1 + product.gstRate / 100)
                                ).toFixed(2)}
                          </strong>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Sale Price (₹)</label>
                  <div className="input-group">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      name="salePrice"
                      value={product.salePrice}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      required
                    />
                    <button
                      type="button"
                      className="btn border d-flex align-items-center "
                      style={{ whiteSpace: "nowrap" }}
                      onClick={() =>
                        setProduct((p) => ({
                          ...p,
                          taxType:
                            p.taxType === "exclusive"
                              ? "inclusive"
                              : "exclusive",
                        }))
                      }
                    >
                      {product.taxType === "exclusive" ? (
                        <>
                          Exclusive <ChevronDown size={16} className="ms-1" />
                        </>
                      ) : (
                        <>
                          Inclusive <ChevronDown size={16} className="ms-1" />
                        </>
                      )}
                    </button>

                    {product.salePrice && product.gstRate ? (
                      <div
                        className="p-2 border rounded-end bg-light"
                        style={{ fontSize: "0.9rem" }}
                      >
                        <div className="d-flex justify-content-between">
                          <strong>
                            ₹
                            {product.taxType === "exclusive"
                              ? (
                                  product.salePrice *
                                  (1 + product.gstRate / 100)
                                ).toFixed(2)
                              : (
                                  product.salePrice /
                                  (1 + product.gstRate / 100)
                                ).toFixed(2)}
                          </strong>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">GST Rate(%)</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      name="gstRate"
                      value={product.gstRate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div> */}
              {/* ---- Discount  ---- */}
              {/* <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Discount (%)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      className="form-control"
                      placeholder="e.g. 10.5"
                      value={product.discount}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (
                          val === "" ||
                          (parseFloat(val) >= 0 && parseFloat(val) <= 100)
                        ) {
                          setProduct((p) => ({ ...p, discount: val }));
                        }
                      }}
                    />
                    <span className="input-group-text">%</span>

                    {product.salePrice && product.discount ? (
                      <div className="input-group-text bg-light">
                        <small>
                          ₹
                          {(
                            product.salePrice *
                            (1 - product.discount / 100)
                          ).toFixed(2)}
                        </small>
                      </div>
                    ) : null}
                  </div>
                  <div className="form-text">Leave empty for no discount</div>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Tax or GST (%)</label>
                  <select
                    className="form-select"
                    name="gstRate"
                    value={product.gstRate}
                    onChange={handleInputChange}
                  >
                    <option value="">Select GST</option>
                    {gstRates.map((r) => (
                      <option key={r} value={r}>
                        {r}%
                      </option>
                    ))}
                  </select>
                </div>
              </div> */}

              {/* ---- Date & Location ---- */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Location</label>
                  <div className="position-relative" ref={locationDropdownRef}>
                    <div
                      className="form-control d-flex align-items-center justify-content-between"
                      onClick={() => setLocationOpen(!locationOpen)}
                      style={{ cursor: "pointer", paddingRight: "2rem" }}
                    >
                      <span className={product.location ? "" : "text-muted"}>
                        {product.location || "Select location"}
                      </span>

                      <FaChevronDown
                        className={`position-absolute end-0 me-3 transition ${
                          locationOpen ? "rotate-180" : ""
                        }`}
                        style={{ fontSize: "0.85rem", pointerEvents: "none" }}
                      />
                    </div>

                    {locationOpen && (
                      <ul
                        className="list-group position-absolute w-100 shadow bg-white rounded"
                        style={{
                          zIndex: 1050,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {locations
                          .filter((loc) =>
                            loc.name
                              .toLowerCase()
                              .includes((searchLocation || "").toLowerCase()),
                          )
                          .map((loc) => (
                            <li
                              key={loc._id}
                              className="list-group-item list-group-item-action py-2 px-3"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setProduct((p) => ({
                                  ...p,
                                  location: loc.name,
                                }));
                                setLocationOpen(false);
                                setSearchLocation("");
                              }}
                            >
                              {loc.name}
                            </li>
                          ))}

                        {/* + Add New Location → Opens Modal */}
                        <li
                          className="list-group-item list-group-item-action text-primary text-center py-2 fw-bold"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setModalType("location");
                            setShowAddModal(true);
                            setLocationOpen(false);
                          }}
                        >
                          + Add New Location
                        </li>
                      </ul>
                    )}

                    {/* Hidden Search Input */}
                    {locationOpen && (
                      <input
                        type="text"
                        autoFocus
                        className="position-absolute start-0 end-0 border-0 outline-0"
                        style={{
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                          cursor: "pointer",
                          zIndex: 1,
                        }}
                        value={searchLocation}
                        onChange={(e) => setSearchLocation(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Search location..."
                      />
                    )}
                  </div>
                </div>
                {/* Expiry Date */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Expiry Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="batch.expiryDate"
                    value={product.batch.expiryDate || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-control"
                  value={product.description || ""}
                  onChange={handleInputChange}
                  placeholder="Add your product description"
                  rows={5}
                />
              </div>
              {/* Dynamic Key-Value Fields */}
              {metaFields.map((item, index) => (
                <div className="row align-items-center mb-3" key={index}>
                  <div className="col-md-5 mb-2 mb-md-0">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Key"
                      value={item.key}
                      onChange={(e) =>
                        handleMetaChange(index, "key", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-5 mb-2 mb-md-0">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Value"
                      value={item.value}
                      onChange={(e) =>
                        handleMetaChange(index, "value", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-2 d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-light btn-sm border text-danger"
                      title="Delete"
                      onClick={() => handleDeleteMetaField(index)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddMetaField}
              >
                + Add
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-3 p-4 mobile-bottom-space-2">
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button variant="primary" className="btn-custom" onClick={handleSubmit}>
          {isEditMode ? "Update Product" : "Add Product"}
        </Button>
      </div>

      <PrefixModal
        show={showAddModal && modalType === "location"}
        onClose={() => {
          setNewModalValue("");
          setShowAddModal(false);
          setModalType("");
        }}
        type="location"
        value={newModalValue}
        onChange={setNewModalValue}
        onSave={handleAddLocation}
        title="Add New Location"
        label="Location Name"
        placeholder="e.g. Freezer, Godown, Kitchen, Warehouse"
        showTypeDropdown={false}
      />
      {/* Modal for Adding Prefix */}
      <PrefixModal
        show={showAddModal && modalType === "prefix"}
        onClose={() => {
          setNewModalValue("");
          setShowAddModal(false);
        }}
        type="prefix"
        value={newModalValue}
        onChange={setNewModalValue}
        onSave={handleAddPrefix}
        title="Add Prefix"
        label="Prefix Name"
        placeholder="Enter item prefix"
        showTypeDropdown={false}
      />
    </div>
  );
};

export default headerLayoutWrapper(AddItem);
